function normalizeCategoryKey(value = '') {
  return String(value)
    .replace(/\s*\/\s*/g, '/')
    .replace(/\/+/g, '/')
    .trim();
}

function getNodeName(node) {
  if (!node)
    return '';

  const getLocalizedText = value => {
    if (!value)
      return '';

    if (typeof value === 'string')
      return value;

    if (typeof value !== 'object')
      return String(value);

    const preferredLanguage = typeof window !== 'undefined' && window.localStorage
      ? (window.localStorage.languageSelected || window.localStorage.language || 'en')
      : 'en';

    const candidates = [preferredLanguage, 'en', 'hu', 'it', 'de'];
    for (const candidate of candidates) {
      if (value[candidate] !== undefined && value[candidate] !== null && value[candidate] !== '')
        return String(value[candidate]);
    }

    const firstKey = Object.keys(value).find(key => value[key] !== undefined && value[key] !== null && value[key] !== '');
    return firstKey ? String(value[firstKey]) : '';
  };

  const name = getLocalizedText(node.name);
  if (name)
    return name;

  const label = getLocalizedText(node.label);
  if (label)
    return label;

  if (node.id !== undefined && node.id !== null)
    return String(node.id);

  return '';
}

function buildCategoryOrderIndex(nodes, parentPath = [], orderIndex = new Map()) {
  if (!Array.isArray(nodes) || !nodes.length)
    return orderIndex;

  nodes.forEach(node => {
    if (!node)
      return;

    const nodeName = getNodeName(node);
    if (!nodeName)
      return;

    const nextPath = parentPath.concat([nodeName]);
    const pathKey = normalizeCategoryKey(nextPath.join('/'));
    if (pathKey && !orderIndex.has(pathKey))
      orderIndex.set(pathKey, orderIndex.size);

    const children = Array.isArray(node.activeMenuCategories) ? node.activeMenuCategories : (Array.isArray(node.children) ? node.children : []);
    buildCategoryOrderIndex(children, nextPath, orderIndex);
  });

  return orderIndex;
}

function sortInventoryRowsByMenuOrder(rows = [], categoryTree = []) {
  const orderIndex = buildCategoryOrderIndex(categoryTree);

  return [...rows].sort((a, b) => {
    const categoryA = normalizeCategoryKey(a && a.category ? a.category : '');
    const categoryB = normalizeCategoryKey(b && b.category ? b.category : '');

    const categoryAOrder = categoryA ? (orderIndex.has(categoryA) ? orderIndex.get(categoryA) : Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
    const categoryBOrder = categoryB ? (orderIndex.has(categoryB) ? orderIndex.get(categoryB) : Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;

    return categoryAOrder - categoryBOrder;
  });
}

function normalizeInventoryExcelHeader(value = '') {
  return String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function getInventoryCountAllowedUnits(quantityType = '') {
  const normalized = String(quantityType || '').trim().toLowerCase();

  if (['cl', 'dl', 'l'].includes(normalized))
    return ['cl', 'dl', 'l'];

  if (['g', 'dkg', 'kg'].includes(normalized))
    return ['g', 'dkg', 'kg'];

  if (['pcs', 'db', 'piece', 'pieces', 'darab', 'dbs'].includes(normalized))
    return ['pcs'];

  return [];
}

function isValidInventoryCountInput(value, quantityType = '') {
  const raw = String(value == null ? '' : value).trim();
  if (!raw)
    return true;

  if (/^[-+]?\d+(?:[.,]\d+)?$/.test(raw.replace(/\s+/g, '')))
    return true;

  const match = raw.match(/^([-+]?\d+(?:[.,]\d+)?)\s*(cl|dl|l|g|dkg|kg|pcs|db|piece|pieces|darab)$/i);
  if (!match)
    return false;

  const unit = match[2].toLowerCase();
  const allowed = getInventoryCountAllowedUnits(quantityType);
  if (!allowed.length)
    return true;

  return allowed.includes(unit) || (allowed.includes('pcs') && ['pcs', 'db', 'piece', 'pieces', 'darab'].includes(unit));
}

function getInventoryCountValidationFormula(quantityType = '', cellRef = 'A1') {
  const allowed = getInventoryCountAllowedUnits(quantityType);
  if (!allowed.length)
    return '=TRUE()';

  const safeCellRef = String(cellRef || 'A1').replace(/\$/g, '');
  const normalizedCell = `SUBSTITUTE(${safeCellRef}," ","")`;
  const unitChecks = allowed.map(unit => {
    const unitText = String(unit || '').toUpperCase();
    return `AND(LEN(${normalizedCell})>${unit.length},ISNUMBER(VALUE(LEFT(${normalizedCell},LEN(${normalizedCell})-${unit.length}))),RIGHT(UPPER(${normalizedCell}),${unit.length})="${unitText}")`;
  }).join(',');

  return `=OR(ISNUMBER(${safeCellRef}),AND(LEN(${normalizedCell})>0,OR(${unitChecks})))`;
}

function escapeInventoryValidationXml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function normalizeInventoryValidationFormula(value) {
  const formula = String(value == null ? '' : value).trim();
  return formula.startsWith('=') ? formula.slice(1) : formula;
}

function buildInventoryDataValidationXml(validationModel = {}) {
  const entries = Object.entries(validationModel || {});
  if (!entries.length)
    return '';

  return entries.map(([sqref, config]) => {
    const validation = config || {};
    const attributes = [];

    const pushAttribute = (name, value) => {
      if (value === undefined || value === null || value === '')
        return;
      attributes.push(`${name}="${escapeInventoryValidationXml(value)}"`);
    };

    pushAttribute('type', validation.type);
    pushAttribute('operator', validation.operator);
    pushAttribute('allowBlank', validation.allowBlank === true ? '1' : (validation.allowBlank === false ? '0' : undefined));
    pushAttribute('showErrorMessage', validation.showErrorMessage === true ? '1' : (validation.showErrorMessage === false ? '0' : undefined));
    pushAttribute('showInputMessage', validation.showInputMessage === true ? '1' : (validation.showInputMessage === false ? '0' : undefined));
    pushAttribute('errorStyle', validation.errorStyle);
    pushAttribute('errorTitle', validation.errorTitle);
    pushAttribute('promptTitle', validation.promptTitle);
    pushAttribute('error', validation.error);
    pushAttribute('prompt', validation.prompt);

    const formulas = [];
    if (validation.formula1 !== undefined)
      formulas.push(`<formula1>${escapeInventoryValidationXml(normalizeInventoryValidationFormula(validation.formula1))}</formula1>`);
    if (validation.formula2 !== undefined)
      formulas.push(`<formula2>${escapeInventoryValidationXml(normalizeInventoryValidationFormula(validation.formula2))}</formula2>`);

    const sqrefValue = Array.isArray(sqref) ? sqref.join(' ') : String(sqref || '');
    return `<dataValidation ${attributes.join(' ')} sqref="${escapeInventoryValidationXml(sqrefValue)}">${formulas.join('')}</dataValidation>`;
  }).join('');
}

function applyInventoryDataValidationXml(xml = '', validationModel = {}) {
  const validationMarkup = buildInventoryDataValidationXml(validationModel);
  if (!validationMarkup)
    return xml;

  const match = /<dataValidations\b[^>]*>[\s\S]*?<\/dataValidations>/;
  if (!match.test(xml))
    return xml;

  const count = Object.keys(validationModel || {}).length;
  return xml.replace(match, `<dataValidations count="${count}">${validationMarkup}</dataValidations>`);
}

function detectInventoryExcelFormat(rowObject = []) {
  if (!Array.isArray(rowObject) || !rowObject.length)
    return false;

  const candidateRows = rowObject.filter(row => row && Object.keys(row).length && Object.values(row).some(value => value !== undefined && value !== null && String(value).trim() !== ''));
  if (!candidateRows.length)
    return false;

  const normalizedKeys = new Set();
  candidateRows.forEach(row => {
    Object.keys(row).forEach(key => normalizedKeys.add(normalizeInventoryExcelHeader(key)));
  });

  const identifierTokens = [
    'menu_item', 'menuitem', 'kategoria', 'category', 'categorie', 'categoria', 'product', 'termek', 'azonosito', 'globalis_azonosito', 'globaalis_azonosito', 'item', 'artikel', 'prodotto', 'name', 'nev', 'nom', 'nome', 'quantity_type', 'mennyiseg', 'mennyiseg', 'mengentyp', 'mertekegyseg', 'unita', 'unit', 'inventory_quantity', 'keszletezesi_mennyiseg', 'quantity'
  ];

  const hasEditableIdentifier = Array.from(normalizedKeys).some(key => {
    const compact = key.replace(/_/g, '');
    return identifierTokens.some(token =>
      key === token
      || key.includes(token)
      || compact.includes(token.replace(/_/g, ''))
      || token.includes(key)
    );
  });

  const countTokens = [
    'count', 'darabszam', 'darabszam_fo', 'quantity', 'menge', 'quantita', 'amount', 'mennyiseg', 'keszletezesi_mennyiseg', 'qty'
  ];

  const hasEditableCountColumn = Array.from(normalizedKeys).some(key => {
    if (!key)
      return false;
    return countTokens.some(token => key.includes(token) || token.includes(key));
  });

  const hasLegacyStoreColumn = Array.from(normalizedKeys).some(key => key.startsWith('s_'));

  return hasEditableIdentifier && hasEditableCountColumn && !hasLegacyStoreColumn;
}

function collectInventoryExcelHeaders(rowObject = []) {
  if (!Array.isArray(rowObject))
    return [];

  const headers = new Set();
  rowObject.forEach(row => {
    if (!row || typeof row !== 'object')
      return;

    Object.keys(row).forEach(key => {
      const value = String(key || '').trim();
      if (value)
        headers.add(value);
    });
  });

  return Array.from(headers);
}

function detectInventoryStoreFromExcelHeaders(headers = [], stores = []) {
  const sourceHeaders = Array.isArray(headers) && headers.length && headers.some(header => header && typeof header === 'object' && !Array.isArray(header))
    ? collectInventoryExcelHeaders(headers)
    : Array.isArray(headers)
      ? headers.filter(Boolean)
      : [];

  if (!sourceHeaders.length)
    return null;

  const normalizedHeaders = sourceHeaders
    .filter(Boolean)
    .map(header => normalizeInventoryExcelHeader(header));

  const countHeaders = normalizedHeaders.filter(header => /(?:^|_)(count|darabszam)/.test(header));

  return (stores || []).find(store => {
    if (!store || (!store.name && !store.label && !store.title && !store.shortName))
      return false;

    const aliases = [store.name, store.label, store.title, store.shortName]
      .filter(Boolean)
      .map(alias => normalizeInventoryExcelHeader(alias));

    return aliases.some(alias => {
      if (!alias)
        return false;

      const aliasTokens = alias.split('_').filter(Boolean);
      if (!aliasTokens.length)
        return false;

      return countHeaders.some(header => aliasTokens.some(token => header.includes(token)));
    });
  }) || null;
}

module.exports = {
  sortInventoryRowsByMenuOrder,
  normalizeCategoryKey,
  buildCategoryOrderIndex,
  detectInventoryExcelFormat,
  detectInventoryStoreFromExcelHeaders,
  collectInventoryExcelHeaders,
  isValidInventoryCountInput,
  getInventoryCountValidationFormula,
  getInventoryCountAllowedUnits,
  buildInventoryDataValidationXml,
  applyInventoryDataValidationXml
};
